import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MoonStar, Sun, X, Clock } from "lucide-react";

/**
 * TeleprompterView — phone-optimized presenter mode.
 * Big-font notes, swipe left/right for slides, timer, red-theme night mode.
 * Broadcasts slide changes over BroadcastChannel to sync with audience window.
 */
export default function TeleprompterView({
  project, currentIndex, setCurrentIndex, elapsedTime, onExit, broadcastChannel,
}) {
  const slides = project?.slides || [];
  const current = slides[currentIndex] || {};
  const next = slides[currentIndex + 1];
  const total = slides.length;

  const [nightMode, setNightMode] = useState(() => (
    typeof window !== "undefined" && localStorage.getItem("teleprompter_night") === "1"
  ));
  const touchStart = useRef(null);
  const containerRef = useRef(null);

  const toggleNight = () => {
    const v = !nightMode;
    setNightMode(v);
    localStorage.setItem("teleprompter_night", v ? "1" : "0");
  };

  const go = (idx) => {
    const clamped = Math.max(0, Math.min(idx, total - 1));
    setCurrentIndex(clamped);
    if (broadcastChannel?.current) {
      broadcastChannel.current.postMessage({ type: "SLIDE_CHANGE", index: clamped });
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") go(currentIndex + 1);
      if (e.key === "ArrowLeft"  || e.key === "PageUp") go(currentIndex - 1);
      if (e.key === "n") toggleNight();
      if (e.key === "Escape") onExit();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, nightMode]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 60) {
      go(currentIndex + (dx < 0 ? 1 : -1));
    }
    touchStart.current = null;
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${String(m).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
  };

  // Two-tone night mode: near-black background + red-tinged text (low-light friendly).
  const theme = nightMode
    ? { bg: "bg-black", text: "text-red-300", subtext: "text-red-500/70", border: "border-red-900/40" }
    : { bg: "bg-slate-950", text: "text-slate-100", subtext: "text-slate-400", border: "border-slate-800" };

  const notesText = current.notes || current.content || "";

  return (
    <div
      ref={containerRef}
      className={`min-h-screen ${theme.bg} ${theme.text} flex flex-col`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="teleprompter-view"
    >
      {/* Top bar */}
      <header className={`border-b ${theme.border} px-4 py-2 flex items-center justify-between`}>
        <button
          onClick={onExit}
          className={`p-2 rounded ${nightMode ? "hover:bg-red-950/30" : "hover:bg-slate-800"}`}
          data-testid="teleprompter-exit"
        >
          <X className="w-5 h-5" />
        </button>
        <div className={`flex items-center gap-2 text-sm ${theme.subtext}`}>
          <Clock className="w-4 h-4" />
          <span className="font-mono">{formatTime(elapsedTime || 0)}</span>
        </div>
        <button
          onClick={toggleNight}
          className={`p-2 rounded ${nightMode ? "hover:bg-red-950/30" : "hover:bg-slate-800"}`}
          data-testid="teleprompter-night-toggle"
          title="Toggle night mode (n)"
        >
          {nightMode ? <Sun className="w-5 h-5" /> : <MoonStar className="w-5 h-5" />}
        </button>
      </header>

      {/* Main notes area — big text */}
      <div className="flex-1 overflow-y-auto p-6 flex flex-col justify-center" data-testid="teleprompter-notes">
        <div className={`text-xs uppercase tracking-widest mb-3 ${theme.subtext}`}>
          Slide {currentIndex + 1} of {total}
        </div>
        {current.title && (
          <h1 className={`font-bold mb-6 ${nightMode ? "text-red-200" : "text-white"}`}
              style={{ fontSize: "clamp(28px, 5vw, 48px)", lineHeight: 1.2 }}>
            {current.title}
          </h1>
        )}
        {notesText && (
          <p className="whitespace-pre-wrap leading-relaxed"
             style={{ fontSize: "clamp(24px, 4vw, 44px)", lineHeight: 1.35 }}>
            {notesText}
          </p>
        )}
        {!current.title && !notesText && (
          <p className={theme.subtext}>No content on this slide.</p>
        )}
      </div>

      {/* Next-up peek */}
      {next && (
        <div className={`px-4 py-3 border-t ${theme.border}`}>
          <div className={`text-[10px] uppercase tracking-widest mb-1 ${theme.subtext}`}>Next up</div>
          <div className={`text-sm truncate ${theme.text} opacity-70`}>
            {next.title || next.content || "Untitled slide"}
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <footer className={`border-t ${theme.border} px-2 py-3 flex items-center justify-between gap-2`}>
        <Button
          onClick={() => go(currentIndex - 1)}
          disabled={currentIndex === 0}
          size="lg"
          variant="outline"
          className={`flex-1 h-14 ${nightMode ? "border-red-900/40 text-red-200 hover:bg-red-950/30" : "border-slate-700"}`}
          data-testid="teleprompter-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        <div className={`px-3 text-sm ${theme.subtext} min-w-[70px] text-center`}>
          {currentIndex + 1}/{total}
        </div>
        <Button
          onClick={() => go(currentIndex + 1)}
          disabled={currentIndex >= total - 1}
          size="lg"
          variant="outline"
          className={`flex-1 h-14 ${nightMode ? "border-red-900/40 text-red-200 hover:bg-red-950/30" : "border-slate-700"}`}
          data-testid="teleprompter-next"
        >
          <ChevronRight className="w-6 h-6" />
        </Button>
      </footer>
    </div>
  );
}
